import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment, 
  Paper, 
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Card,
  CardContent,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  AppBar,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  ChevronDown,
  ChevronRight,
  Mail,
  RefreshCw,
  Building,
  Users,
  User,
  Calendar,
  FileText,
  Send,
  X,
  AlertCircle
} from 'lucide-react';
import dayjs from "dayjs";

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

// Calculate days elapsed since form creation
const calculateDaysElapsed = (createdDate) => {
  if (!createdDate) return 0;
  
  const created = new Date(createdDate);
  const today = new Date();
  
  // Check if date is valid
  if (isNaN(created.getTime())) return 0;
  
  const diffTime = today - created;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays); // Ensure non-negative
};

// Get status color based on days elapsed
const getStatusColor = (days) => {
  if (days <= 3) return '#4CAF50'; // Green - recent
  if (days <= 7) return '#FF9800'; // Orange - a week
  return '#F44336'; // Red - over a week
};

// Send email to one or multiple employees
const sendReminderEmail = async (employeeIds, pendingForms = [], excludedEmployees = []) => {
  const token = localStorage.getItem('token');
  
  // Filter out excluded employees
  const filteredEmployeeIds = Array.isArray(employeeIds)
    ? employeeIds.filter(id => !excludedEmployees.includes(id))
    : [employeeIds];
  
  if (filteredEmployeeIds.length === 0) {
    throw new Error('No employees selected for email');
  }
  
  try {
    const response = await fetch(`${API_URL}/bulk-send-form-reminder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        employeeIds: filteredEmployeeIds, 
        pendingForms 
      })
    });

    if (!response.ok) {
      throw new Error('Failed to send reminder emails');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending reminder emails:', error);
    throw error;
  }
};

// Email Composition Dialog
const ReminderEmailDialog = ({ open, onClose, employees, onSend, loading }) => {
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    // Initially select all employees
    if (employees && employees.length > 0) {
      setSelectedEmployees(employees.map(emp => emp.id));
    }
  }, [employees]);

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (employees && employees.length > 0) {
      if (selectedEmployees.length === employees.length) {
        // Deselect all
        setSelectedEmployees([]);
      } else {
        // Select all
        setSelectedEmployees(employees.map(emp => emp.id));
      }
    }
  };

  const handleSend = () => {
    onSend(selectedEmployees);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(3px)'
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#EBF5FF', // light blue background
          borderBottom: '1px solid #e0e0e0',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        
        <Box display="flex" alignItems="center">
          <Mail size={20} color="#2563EB" style={{ marginRight: 8 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
            Send Form Reminder Emails
          </Typography>
        </Box>
        <IconButton onClick={onClose} edge="end" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3, px: 3 }}>
        <DialogContentText paragraph sx={{ color: '#4B5563', mb: 3 }}>
          Select the employees you want to send reminder emails to regarding their incomplete forms:
        </DialogContentText>
        
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={employees && selectedEmployees.length === employees.length} 
                indeterminate={employees && selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                onChange={handleSelectAll} 
                color="primary"
              />
            }
            label={<Typography sx={{ fontSize: '0.875rem', color: '#4B5563' }}>Select All</Typography>}
          />
        </Box>
        
        <Paper 
          variant="outlined" 
          sx={{ 
            maxHeight: 300, 
            overflow: 'auto', 
            mb: 3,
            border: '1px solid #E5E7EB', 
            borderRadius: 2
          }}
        >
          <List dense disablePadding>
            {employees && employees.map((employee) => (
              <ListItemButton 
                key={employee.id} 
                onClick={() => handleEmployeeToggle(employee.id)}
                sx={{ 
                  py: 1.5,
                  borderBottom: '1px solid #F3F4F6',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: '#F9FAFB' }
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedEmployees.includes(employee.id)} 
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleEmployeeToggle(employee.id)}
                      color="primary"
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.875rem', color: '#1F2937' }}>{employee.firstName} {employee.lastName}</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {employee.incompleteCount} form(s) pending
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Paper>
        
        <Paper 
          sx={{ 
            mt: 3, 
            bgcolor: '#F9FAFB', 
            p: 2, 
            borderRadius: 2, 
            border: '1px solid #E5E7EB'
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#4B5563' }}>
            Email Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '0.75rem' }}>
            Subject: Action Required: Complete Your Onboarding Forms
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            This email will remind employees to complete their pending forms and provide them with a link to log in to the employee portal.
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions 
        sx={{ 
          bgcolor: '#F9FAFB', 
          p: 2, 
          borderTop: '1px solid #E5E7EB',
          justifyContent: 'flex-end'
        }}
      >
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
          sx={{ 
            color: '#4B5563', 
            borderColor: '#D1D5DB',
            '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
            textTransform: 'none',
            mr: 1
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSend}
          disabled={selectedEmployees.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send size={16} />}
          sx={{ 
            bgcolor: '#2563EB',
            '&:hover': { bgcolor: '#1D4ED8' },
            textTransform: 'none'
          }}
        >
          {loading ? 'Sending...' : `Send to ${selectedEmployees.length} Employee(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Employee Form Details Dialog
const EmployeeFormsDialog = ({ open, onClose, employee }) => {
  if (!employee) return null;
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(3px)'
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#EBF5FF',
          borderBottom: '1px solid #e0e0e0',
          p: 2, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center">
          <User size={20} color="#2563EB" style={{ marginRight: 8 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
            {employee.firstName} {employee.lastName}'s Pending Forms
          </Typography>
        </Box>
        <IconButton onClick={onClose} edge="end" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3, px: 3, overflow: 'auto' }}>
        <List disablePadding>
          {employee.incompleteForms && employee.incompleteForms.map((form, index) => {
            const daysElapsed = form.createdAt ? calculateDaysElapsed(form.createdAt) : null;
            return (
              <Card 
                key={index} 
                variant="outlined" 
                sx={{ 
                  mb: 2, 
                  border: '1px solid #E5E7EB',
                  borderRadius: 2,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <FileText size={18} color="#2563EB" style={{ marginRight: 8 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#1F2937' }}>
                        {form.formName}
                      </Typography>
                    </Box>
                    {daysElapsed !== null && (
                      <Chip 
                        label={daysElapsed === 0 
                          ? 'Today' 
                          : `${daysElapsed} day${daysElapsed !== 1 ? 's' : ''} ago`
                        }
                        size="small"
                        sx={{ 
                          bgcolor: getStatusColor(daysElapsed),
                          color: 'white',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}
                  </Box>
                  
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                    <Calendar size={14} style={{ marginRight: 4 }} color="#6B7280" />
                    <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                      {form.createdAt 
                        ? `Created: ${dayjs(form.createdAt).format("MM/DD/YYYY")}`
                        : 'Pending'
                      }
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          
          {(!employee.incompleteForms || employee.incompleteForms.length === 0) && (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                No detailed form information available
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>

      <DialogActions
        sx={{ 
          bgcolor: '#F9FAFB', 
          p: 2, 
          borderTop: '1px solid #E5E7EB',
          mt: 'auto'
        }}
      >
        <Button 
          onClick={onClose} 
          variant="contained"
          color="primary"
          sx={{ 
            bgcolor: '#2563EB',
            '&:hover': { bgcolor: '#1D4ED8' },
            textTransform: 'none'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function IncompleteFormsTracker() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // States
  const [stores, setStores] = useState([]);
  const [storeEmployees, setStoreEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedStores, setExpandedStores] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [storeSearchTerms, setStoreSearchTerms] = useState({});
  
  // Email states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [sending, setSending] = useState(false);
  
  // Employee details states
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Notification
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Show snackbar helper
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch stores and incomplete forms data
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userStore = localStorage.getItem('userStore');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const isManager = userData.role === 'manager';
      
      // Fetch locations/stores
      let storesResponse;
      if (isManager && userStore) {
        // If manager, only get their store
        storesResponse = await fetch(`${API_URL}/locations?id=${userStore}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        // For admin, get all stores
        storesResponse = await fetch(`${API_URL}/locations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      
      if (!storesResponse.ok) {
        console.error('Failed to fetch locations:', storesResponse.status);
        throw new Error('Failed to fetch locations');
      }
      
      const storesData = await storesResponse.json();
      console.log('Fetched stores:', storesData);
      setStores(storesData || []);
      
      // Create an object to store employees by store
      const employeesByStore = {};
      
      // For each store, fetch employees and their incomplete forms
      for (const store of storesData) {
        // First, fetch all employees for this location
        const employeesResponse = await fetch(`${API_URL}/locations/${store.id}/employees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!employeesResponse.ok) {
          console.error(`Failed to fetch employees for store ${store.id}:`, employeesResponse.status);
          continue;
        }
        
        const employeesData = await employeesResponse.json();
        console.log(`Fetched ${employeesData.length} employees for store ${store.name}`);
        
        // Now fetch incomplete forms for this location
        const formsResponse = await fetch(`${API_URL}/locations/${store.id}/incomplete-forms`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!formsResponse.ok) {
          console.error(`Failed to fetch incomplete forms for store ${store.id}:`, formsResponse.status);
          continue;
        }
        
        const formsData = await formsResponse.json();
        console.log(`Fetched ${formsData.length} incomplete forms for store ${store.name}`);
        
        // Create a map of employees with their incomplete forms
        const employeeMap = {};
        
        // Initialize employees
        employeesData.forEach(emp => {
          employeeMap[emp.id] = {
            id: emp.id,
            firstName: emp.firstName || '',
            lastName: emp.lastName || '',
            name: emp.name || `${emp.firstName} ${emp.lastName}`.trim(),
            position: emp.position,
            incompleteCount: 0,
            incompleteForms: [],
            oldestPendingFormDate: null
          };
        });
        
        // Add incomplete forms to employees
        formsData.forEach(form => {
          if (employeeMap[form.employeeId]) {
            employeeMap[form.employeeId].incompleteCount++;
            employeeMap[form.employeeId].incompleteForms.push({
              formId: form.formId,
              formName: form.formName,
              createdAt: form.createdAt
            });
            
            // Track oldest pending form date
            if (form.createdAt) {
              if (!employeeMap[form.employeeId].oldestPendingFormDate || 
                  new Date(form.createdAt) < new Date(employeeMap[form.employeeId].oldestPendingFormDate)) {
                employeeMap[form.employeeId].oldestPendingFormDate = form.createdAt;
              }
            }
          }
        });
        
        // Filter to only include employees with incomplete forms
        const employeesWithIncompleteForms = Object.values(employeeMap).filter(emp => emp.incompleteCount > 0);
        
        if (employeesWithIncompleteForms.length > 0) {
          console.log(`Store ${store.name} has ${employeesWithIncompleteForms.length} employees with incomplete forms`);
          employeesByStore[store.id] = employeesWithIncompleteForms;
        }
      }
      
      setStoreEmployees(employeesByStore);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Toggle store expansion
  const toggleStoreExpansion = (storeId) => {
    setExpandedStores(prev => ({
      ...prev,
      [storeId]: !prev[storeId]
    }));
  };

  // Handle store employee search
  const handleStoreSearch = (storeId, term) => {
    setStoreSearchTerms(prev => ({
      ...prev,
      [storeId]: term
    }));
  };

  // Handle sending email to multiple employees
  const handleSendEmail = (employees) => {
    setSelectedEmployees(employees);
    setEmailDialogOpen(true);
  };

  // Process email sending
  const processEmailSending = async (selectedEmployeeIds) => {
    setSending(true);
    try {
      await sendReminderEmail(selectedEmployeeIds, []);
      
      showSnackbar('Reminder emails sent successfully');
      setEmailDialogOpen(false);
    } catch (error) {
      console.error('Failed to send emails:', error);
      showSnackbar('Failed to send reminder emails', 'error');
    } finally {
      setSending(false);
    }
  };

  // Handle employee click to show form details
  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeDialogOpen(true);
  };

  // Filter stores by search term
  const filteredStores = stores.filter(store => {
    // Check if the store has employees with incomplete forms
    const hasEmployees = storeEmployees[store.id] && storeEmployees[store.id].length > 0;
    
    // Only include stores with incomplete forms
    if (!hasEmployees) return false;
    
    // If there's a search term, check if any employee name matches
    if (searchTerm.trim()) {
      return storeEmployees[store.id].some(emp => 
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return true;
  });

  // Filter employees by store search term
  const getFilteredEmployees = (storeId) => {
    const employees = storeEmployees[storeId] || [];
    const searchTerm = storeSearchTerms[storeId] || '';
    
    if (!searchTerm.trim()) return employees;
    
    return employees.filter(emp => 
      `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Calculate total number of employees and forms
  const totalEmployees = Object.values(storeEmployees).reduce(
    (sum, employees) => sum + employees.length, 
    0
  );
  
  const totalIncompleteForms = Object.values(storeEmployees).reduce(
    (sum, employees) => sum + employees.reduce((empSum, emp) => empSum + (Number(emp.incompleteCount) || 0), 0), 
    0
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif'
    }}>
      {/* Header */}
      <AppBar 
      position="static" 
      color="default" 
      elevation={0}
      sx={{ 
        borderBottom: '1px solid #E5E7EB',
        backgroundColor: 'white',
        px: isMobile ? 2 : 3,
        mb: isMobile ? 18 : 3
      }}
    >
      <Toolbar sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center' }}>
        {/* Title */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937', width: isMobile ? '100%' : 'auto' }}>
          Incomplete Forms Tracker
        </Typography>

        {/* Action Buttons and Search */}
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: isMobile ? 1.5 : 2, width: isMobile ? '100%' : 'auto' }}>
          {/* Search Field */}
          <TextField
            placeholder="Search employees..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#9CA3AF" />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: 1,
                width: '100%'
              }
            }}
            sx={{ width: isMobile ? '100%' : 240 }}
          />

          {/* Send to All Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSendEmail(Object.values(storeEmployees).flat())}
            startIcon={<Mail size={16} />}
            disabled={totalEmployees === 0}
            sx={{ 
              bgcolor: '#2563EB',
              '&:hover': { bgcolor: '#1D4ED8' },
              textTransform: 'none',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              borderRadius: 1,
              width: isMobile ? '100%' : 'auto',
              padding: 1,

            }}
          >
            Send to All
          </Button>

          {/* Refresh Button */}
          <Button
            variant="outlined"
            onClick={fetchData}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshCw size={16} />}
            disabled={loading}
            sx={{ 
              color: '#4B5563', 
              borderColor: '#D1D5DB',
              '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
              textTransform: 'none',
              borderRadius: 1,
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Refresh
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
      
      {/* Main content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: 3, 
        bgcolor: '#F9FAFB'
      }}>
        {/* Status overview */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #E5E7EB'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2563EB', mb: 0.5 }}>
                Status Overview
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                {filteredStores.length} store(s) with incomplete forms
              </Typography>
            </Box>
            
            <Box display="flex" mt={{ xs: 2, sm: 0 }} gap={1}>
              <Chip 
                icon={<Users size={16} />}
                label={`${totalEmployees} Employee(s)`} 
                sx={{ 
                  bgcolor: '#EBF5FF', 
                  color: '#1E40AF',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#3B82F6' } 
                }}
              />
              <Chip 
                icon={<AlertCircle size={16} />}
                label={`${totalIncompleteForms} Form(s)`} 
                color="error" 
                sx={{ 
                  bgcolor: '#FEE2E2', 
                  color: '#B91C1C',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#EF4444' } 
                }}
              />
            </Box>
          </Box>
        </Paper>
        
        {/* Store listings */}
        <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#3B82F6' }} />
            </Box>
          ) : (
            <>
              {filteredStores.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {filteredStores.map(store => {
                    const storeEmployeeList = storeEmployees[store.id] || [];
                    const filteredEmployeeList = getFilteredEmployees(store.id);
                    const totalIncompleteForms = storeEmployeeList.reduce(
                      (sum, emp) => sum + (Number(emp.incompleteCount) || 0), 0
                    );

                    return (
                      <Paper 
                      key={store.id} 
                      elevation={0}
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        border: '1px solid #E5E7EB',
                        maxWidth: '100%' // Ensure it does not extend beyond screen width
                      }}
                    >
                      <Box 
                        onClick={() => toggleStoreExpansion(store.id)} 
                        sx={{ 
                          px: 3,
                          py: 2,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile, row on larger screens
                          alignItems: { xs: 'flex-start', sm: 'center' }, // Align properly
                          justifyContent: 'space-between',
                          bgcolor: 'white'
                        }}
                      >
                        {/* Left Section (Icon + Name + Chips) */}
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
                            <Building size={20} color="#2563EB" style={{ marginRight: 8 }} />
                    
                            <Typography 
                              variant="h6" 
                              component="div"
                              sx={{ 
                                fontWeight: 500, 
                                color: '#1F2937',
                                fontSize: { xs: '1rem', sm: '1.125rem' }, // Responsive font size
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {store.name}
                            </Typography>
                          </Box>
                    
                          {/* Stack Chips Below on Mobile */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: { xs: 1, sm: 0 } }}>
                            <Chip 
                              label={`${storeEmployeeList.length} Employee(s)`} 
                              size="small" 
                              sx={{ 
                                bgcolor: '#EBF5FF', 
                                color: '#1E40AF',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                    
                            <Chip 
                              label={`${totalIncompleteForms} Incomplete Form(s)`} 
                              size="small" 
                              sx={{ 
                                bgcolor: '#FEE2E2', 
                                color: '#B91C1C',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                          </Box>
                        </Box>
                    
                        {/* Always visible toggle arrow */}
                        <IconButton 
                          size="small" 
                          sx={{ color: '#9CA3AF', alignSelf: { xs: 'flex-end', sm: 'center' } }}
                        >
                          {expandedStores[store.id] ? (
                            <ChevronDown size={20} />
                          ) : (
                            <ChevronRight size={20} />
                          )}
                        </IconButton>
                      </Box>
                    
                      <Collapse in={expandedStores[store.id]} timeout="auto" unmountOnExit>
                        <Divider />
                        <Box sx={{ px: 3, py: 2, bgcolor: 'white' }}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            
                            {/* Responsive Search Field */}
                            <TextField
                              placeholder="Search employees..."
                              size="small"
                              value={storeSearchTerms[store.id] || ''}
                              onChange={(e) => handleStoreSearch(store.id, e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Search size={16} color="#9CA3AF" />
                                  </InputAdornment>
                                ),
                                sx: { borderRadius: 1 }
                              }}
                              sx={{ width: { xs: '100%', sm: 250 } }} // Full width on mobile
                            />
                    
                            {/* Responsive Button */}
                            <Button 
                              variant="outlined" 
                              startIcon={<Mail size={16} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendEmail(storeEmployeeList);
                              }}
                              disabled={storeEmployeeList.length === 0}
                              sx={{ 
                                color: '#4B5563', 
                                borderColor: '#D1D5DB',
                                '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
                                textTransform: 'none',
                                borderRadius: 1,
                                width: { xs: '100%', sm: 'auto' } // Full width when needed
                              }}
                            >
                              Send to Store
                            </Button>
                          </Box>
                    
                          {/* Employee List */}
                          <List disablePadding>
                            {filteredEmployeeList.map((employee) => {
                              const daysElapsed = employee.oldestPendingFormDate 
                                ? calculateDaysElapsed(employee.oldestPendingFormDate)
                                : null;
                              
                              return (
                                <ListItemButton
                                  key={employee.id}
                                  onClick={() => handleEmployeeClick(employee)}
                                  sx={{
                                    py: 1.5,
                                    borderBottom: '1px solid #F3F4F6',
                                    '&:last-child': { borderBottom: 'none' },
                                    '&:hover': { bgcolor: '#F9FAFB' },
                                    display: 'flex',
                                    flexWrap: 'wrap'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                                    <User size={18} color="#2563EB" style={{ marginRight: 8 }} />
                                    
                                    <Typography 
                                      variant="body1" 
                                      sx={{ fontWeight: 500, color: '#1F2937', flexShrink: 1, maxWidth: '60%' }}
                                    >
                                      {employee.firstName || employee.name || 'Unknown'} {employee.lastName || ''}
                                    </Typography>
                                    
                                    <Chip
                                      label={daysElapsed !== null 
                                        ? `${employee.incompleteCount} form(s)` 
                                        : `${employee.incompleteCount} form(s)`
                                      }
                                      size="small"
                                      sx={{
                                        ml: 'auto',
                                        bgcolor: daysElapsed !== null ? getStatusColor(daysElapsed) : '#9CA3AF',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  </Box>
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Box>
                      </Collapse>
                    </Paper>
                    
                    
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No stores with incomplete forms found.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Email Dialog */}
        <ReminderEmailDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          employees={selectedEmployees}
          onSend={processEmailSending}
          loading={sending}
        />

        {/* Employee Forms Dialog */}
        <EmployeeFormsDialog
          open={employeeDialogOpen}
          onClose={() => setEmployeeDialogOpen(false)}
          employee={selectedEmployee}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};